import React, { useEffect, useRef, useState } from "react";
import { T } from "../../app/tokens.js";

// Shared "Sort" control — primary "Sort by" + secondary "Then by" + direction
// toggles for each. Originally built inline in MarketPlanPage.jsx
// (LIST-SORT-ROLLOUT-S1 extracted it here so Offices and Leases can reuse it
// unchanged). Behavior/markup/testids are preserved exactly so existing
// Market Plan tests keep passing.
//
// Props:
//   fields       — [{ value, label }]  suggested/available sort fields
//   primary      — { key, dir }        controlled primary sort
//   secondary    — { key, dir }        controlled secondary ("Then by") sort; key may be null
//   onChange     — ({ primary, secondary }) => void
//   testId       — optional data-testid prefix for the trigger button
//   defaultKey   — primary key value considered "default" (button shows plain "Sort" for it)

export function SortIcon({ active }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke={active ? "var(--v2-accent)" : "var(--v2-mid)"}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="14" y2="6" />
      <line x1="4" y1="12" x2="11" y2="12" />
      <line x1="4" y1="18" x2="8" y2="18" />
      <polyline points="17 8 20 5 20 5 23 8" />
      <line x1="20" y1="5" x2="20" y2="20" />
    </svg>
  );
}

export function SortControl({ fields = [], primary, secondary, onChange, testId = "plan-sort", defaultKey = null, showSecondary = true, compactLabel = false }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const primaryField = fields.find((f) => f.value === primary.key);
  const isActive = !!primaryField && primary.key !== defaultKey;
  const primaryLabel = primaryField?.label;

  function setPrimaryKey(key) {
    // Pick the field's first-selection default direction whenever the sort
    // field changes (mirrors clicking a new column header) — otherwise the
    // previous field's direction carries over, e.g. selecting "Market name"
    // after the People-descending default would show Z→A instead of A→Z.
    // Numeric fields (fields marked `numeric: true`) default to descending
    // (biggest/soonest first); everything else defaults to ascending.
    const field = fields.find((f) => f.value === key);
    onChange({ primary: { key, dir: field?.numeric ? "desc" : "asc" }, secondary });
  }
  function togglePrimaryDir() {
    onChange({ primary: { key: primary.key, dir: primary.dir === "asc" ? "desc" : "asc" }, secondary });
  }
  function setSecondaryKey(key) {
    onChange({ primary, secondary: { key: key || null, dir: secondary.dir } });
  }
  function toggleSecondaryDir() {
    onChange({ primary, secondary: { key: secondary.key, dir: secondary.dir === "asc" ? "desc" : "asc" } });
  }

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        data-testid={`${testId}-btn`}
        title={compactLabel && isActive ? `Sort: ${primaryLabel}` : undefined}
        aria-label={compactLabel && isActive ? `Sort: ${primaryLabel}` : undefined}
        onClick={() => setOpen((o) => !o)}
        style={{
          display:      "inline-flex",
          alignItems:   "center",
          gap:          7,
          border:       `1px solid ${isActive ? "var(--v2-blue-border)" : "var(--v2-line)"}`,
          background:   isActive ? "var(--v2-blue-tint)" : "var(--v2-card)",
          borderRadius: 9,
          padding:      "8px 13px",
          fontSize:     T.fsSm5,
          fontWeight:   isActive ? T.fwBold : T.fwSemibold,
          color:        isActive ? "var(--v2-accent)" : "var(--v2-mid)",
          cursor:       "pointer",
          transition:   "all .15s",
          whiteSpace:   "nowrap",
        }}
      >
        <SortIcon active={isActive} />
        Sort{isActive && !compactLabel ? `: ${primaryLabel}` : ""}
      </button>

      {open && (
        <div
          data-testid={`${testId}-panel`}
          style={{
            position:     "absolute",
            top:          "calc(100% + 6px)",
            left:         0,
            zIndex:       80,
            background:   "var(--v2-card)",
            border:       "1px solid var(--v2-line)",
            borderRadius: 14,
            boxShadow:    "var(--v2-shadow-drop)",
            padding:      "14px 16px",
            minWidth:     280,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
            <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark }}>Sort by</label>
            <div style={{ display: "flex", gap: 6 }}>
              <select
                data-testid={`${testId}-primary-select`}
                value={primary.key ?? ""}
                onChange={(e) => setPrimaryKey(e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: "6px 8px", borderRadius: 7, border: `1px solid ${T.border}`, fontSize: T.fsBase, color: T.dark, background: T.white }}
              >
                {fields.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <button
                type="button"
                data-testid={`${testId}-primary-dir-btn`}
                onClick={togglePrimaryDir}
                aria-label={primary.dir === "asc" ? "Ascending" : "Descending"}
                style={{ width: 32, height: 32, flexShrink: 0, borderRadius: 7, border: `1px solid ${T.border}`, background: T.white, cursor: "pointer", fontSize: T.fsBase, color: T.dark }}
              >
                {primary.dir === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>

          {showSecondary && <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
            <label style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark }}>Then by</label>
            <div style={{ display: "flex", gap: 6 }}>
              <select
                data-testid={`${testId}-secondary-select`}
                value={secondary.key ?? ""}
                onChange={(e) => setSecondaryKey(e.target.value)}
                style={{ flex: 1, minWidth: 0, padding: "6px 8px", borderRadius: 7, border: `1px solid ${T.border}`, fontSize: T.fsBase, color: T.dark, background: T.white }}
              >
                <option value="">None</option>
                {fields.filter((f) => f.value !== primary.key).map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
              <button
                type="button"
                data-testid={`${testId}-secondary-dir-btn`}
                onClick={toggleSecondaryDir}
                disabled={!secondary.key}
                aria-label={secondary.dir === "asc" ? "Ascending" : "Descending"}
                style={{ width: 32, height: 32, flexShrink: 0, borderRadius: 7, border: `1px solid ${T.border}`, background: T.white, cursor: secondary.key ? "pointer" : "default", opacity: secondary.key ? 1 : 0.4, fontSize: T.fsBase, color: T.dark }}
              >
                {secondary.dir === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>}

          <div style={{ fontSize: T.fsXxs, color: T.light, lineHeight: 1.4 }}>
            Suggested: {fields.map((f) => f.label).join(" · ")}
          </div>
        </div>
      )}
    </div>
  );
}
